import { useAuthContext } from '@context/auth/useAuthContext';
import { Dropdown } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, useState } from 'react';
import useMarkNotificationAsRead from '../hooks/useMarkNotifications';
import useOptimisticUpdates from '../hooks/useOptimisticUpdates';
import useNotifications from '../hooks/useWidgetNotifications';
import { SUPPORTED_NOTIFICATION_TYPES } from '../consts/supportedNotificationTypes';
import AllNotificationsButton from './components/AllNotificationsButton';
import NotificationsBell from './components/NotificationsBell';
import WidgetNotificationHeader from './components/WidgetNotificationHeader';
import WidgetNotificationsList from './components/WidgetNotificationsList';
import { BACKDROP_ANIMATIONS } from './consts/motionAnimations';

const NotificationsWidget = () => {
  const { user } = useAuthContext();
  const [open, setOpen] = useState(false);

  const { notifications, lastFetchedTime, areNotificationsFetching, areNotificationsLoading } = useNotifications(
    user.userId
  );

  // TODO : Remove this filtering when the notifications are more stable. The only
  // reason for filtering at the moment is that the other notifications do not have
  // any actual meaning for the admins.
  const filteredOnlyVisitNotifications = notifications.filter((n) => SUPPORTED_NOTIFICATION_TYPES.includes(n.type));

  // Counted over the FILTERED list, because that is what the panel shows. Counting
  // the raw one put unread messages on the bell that opening it could not display
  // and reading could not clear - on dev, 20 unread shift_updated for office users,
  // a badge with nothing behind it.
  const unReadNotifications = filteredOnlyVisitNotifications.filter((n) => !n.isRead).length;

  const { markAllNotificationsAsRead } = useMarkNotificationAsRead();
  const { optimisticMarkAllAsReadForWidgetNotifications, optimisticMarkAllAsReadForFullNotificationPage } =
    useOptimisticUpdates({
      userId: user.userId,
    });

  const onMarkAllAsRead = () => {
    // If we have no unread, we don't need to do anything
    if (!unReadNotifications) return;
    // We change in memory for a better UX
    optimisticMarkAllAsReadForFullNotificationPage();
    optimisticMarkAllAsReadForWidgetNotifications();
    // We notify the BE
    markAllNotificationsAsRead({ userId: user.userId });
  };

  return (
    <div className="relative">
      <AnimatePresence>
        {open && (
          <motion.div
            key="backdrop"
            className="fixed inset-0 z-[999] bg-black/20 backdrop-blur-[0.4px]"
            onClick={() => setOpen(false)}
            {...BACKDROP_ANIMATIONS}
          />
        )}
      </AnimatePresence>

      <Dropdown
        open={open}
        placement="bottomRight"
        popupRender={() => (
          <div
            key="notifications-panel"
            className="flex w-[400px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl ring-1 ring-black/5"
          >
            <WidgetNotificationHeader
              lastUpdatedAt={lastFetchedTime}
              areAllNotificationsRead={unReadNotifications === 0}
              onMarkAllAsRead={onMarkAllAsRead}
            />

            <WidgetNotificationsList
              userId={user.userId}
              closeNotificationsPanel={() => setOpen(false)}
              notifications={filteredOnlyVisitNotifications}
              areNotificationsFetching={areNotificationsFetching}
              areNotificationsLoading={areNotificationsLoading}
            />

            <AllNotificationsButton closeNotificationsPanel={() => setOpen(false)} />
          </div>
        )}
      >
        <NotificationsBell count={unReadNotifications} onClick={() => setOpen((v) => !v)} />
      </Dropdown>
    </div>
  );
};

export default memo(NotificationsWidget);
